import {
  arrow,
  flip,
  FloatingArrow,
  FloatingPortal,
  offset,
  shift,
  useFloating
} from '@floating-ui/react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import { ActiveTipSequenceType } from 'data/types';
import {
  dismissActiveTip,
  nextTip,
  previousTip,
  skipAllTips
} from 'features/ui';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import {
  getFallbackTipAnchorRect,
  saveDismissedTips,
  saveTipsSkipped
} from 'utils/tips';

const ARROW_WIDTH = 16;
const ARROW_HEIGHT = 8;
const CARD_BORDER_RADIUS = 12;
const CARD_RING_COLOR = 'rgba(0, 0, 0, 0.05)';

type TipsPanelProps = {
  readonly sequence: ActiveTipSequenceType;
  readonly anchorRect: DOMRect | null;
};

export default function TipsPanel({ sequence, anchorRect }: TipsPanelProps) {
  const dispatch = useAppDispatch();
  const dismissedTips = useAppSelector((state) => state.ui.dismissedTips);
  const arrowRef = useRef<SVGSVGElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);

  const { refs, floatingStyles, context, isPositioned, update } = useFloating({
    placement: 'right-start',
    middleware: [
      offset(ARROW_HEIGHT + 12),
      flip({
        padding: 12,
        fallbackPlacements: ['left-start', 'bottom-start', 'top-start']
      }),
      shift({ padding: 12, crossAxis: true }),
      arrow({ element: arrowRef, padding: CARD_BORDER_RADIUS })
    ]
  });

  useLayoutEffect(() => {
    refs.setReference({
      getBoundingClientRect: () => anchorRect ?? getFallbackTipAnchorRect()
    });
  }, [anchorRect, refs]);

  const setCardRef = useCallback(
    (node: HTMLDivElement | null) => {
      cardRef.current = node;
      refs.setFloating(node);
    },
    [refs]
  );

  const { tips, tipIndex, stepNumber, stepCount, stepLabel, dismissKey } =
    sequence;
  const currentTip = tips[tipIndex];

  // Only tips with a title render a card (transient highlights like the
  // fenestration focus flash have none and must not lock the screen).
  const isShown = !!currentTip?.title;

  // While a tip card is shown the rest of the screen is blocked for pointer
  // input by the backdrop below; keep keyboard focus inside the card too so
  // background controls can't be reached or typed into.
  useEffect(() => {
    if (!isShown || !isPositioned) {
      return undefined;
    }

    const containFocus = () => {
      const card = cardRef.current;
      const active = document.activeElement;
      if (card && active instanceof Node && !card.contains(active)) {
        card.focus({ preventScroll: true });
      }
    };

    containFocus();
    document.addEventListener('focusin', containFocus);
    return () => {
      document.removeEventListener('focusin', containFocus);
    };
  }, [isShown, isPositioned]);

  if (!currentTip?.title) {
    return null;
  }

  const showEyebrow =
    stepNumber !== undefined && stepCount !== undefined && !!stepLabel;
  const isMultiTip = tips.length >= 2;
  const isFirstTip = tipIndex === 0;
  const isLastTip = tipIndex === tips.length - 1;
  const showDots = isMultiTip;
  const showSkipTips = isMultiTip && isFirstTip;
  const showBack = isMultiTip && !isFirstTip;

  const handleGotIt = () => {
    if (dismissKey && !dismissedTips.includes(dismissKey)) {
      saveDismissedTips([...dismissedTips, dismissKey]);
    }
    dispatch(dismissActiveTip());
  };

  const handleSkipTips = () => {
    saveTipsSkipped(true);
    dispatch(skipAllTips());
  };

  return (
    <FloatingPortal>
      {/* Invisible backdrop that swallows pointer events so the app (including
          the highlighted elements) can't be used until the tips are finished
          or dismissed. Sits above the dim overlay, below the card. */}
      <div
        data-testid='tips-interaction-blocker'
        aria-hidden='true'
        className='fixed inset-0'
        style={{ zIndex: 9998 }}
      />
      <div
        ref={setCardRef}
        role='dialog'
        aria-modal='true'
        aria-label={currentTip.title}
        tabIndex={-1}
        className='tips-fade-in w-80 rounded-xl bg-white p-4 shadow-2xl outline-none ring-1 ring-black/5'
        style={{
          ...floatingStyles,
          zIndex: 9999,
          visibility: isPositioned ? 'visible' : 'hidden'
        }}
      >
        {anchorRect && (
          <FloatingArrow
            ref={arrowRef}
            context={context}
            width={ARROW_WIDTH}
            height={ARROW_HEIGHT}
            fill='white'
            stroke={CARD_RING_COLOR}
            strokeWidth={1}
          />
        )}
        {showEyebrow && (
          <div className='text-xs font-bold uppercase tracking-wide text-green-700'>
            {`Step ${stepNumber} of ${stepCount} \u00b7 ${stepLabel}`}
          </div>
        )}
        <div className='mt-1 text-lg font-bold text-gray-900'>
          {currentTip.title}
        </div>
        {currentTip.description && (
          <p className='mt-1 text-sm leading-relaxed text-gray-600'>
            {currentTip.description}
          </p>
        )}
        {currentTip.image && (
          <img
            src={currentTip.image}
            alt=''
            aria-hidden='true'
            className='mt-3 h-auto w-full rounded-md border border-gray-200'
            onLoad={() => update()}
          />
        )}
        <div className='mt-3 flex items-center gap-4'>
          {showDots && (
            <div className='flex shrink-0 items-center gap-1'>
              {tips.map((_, index) => (
                <span
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  className={
                    index === tipIndex
                      ? 'h-1.5 w-1.5 rounded-full bg-green-600'
                      : 'h-1.5 w-1.5 rounded-full bg-gray-300'
                  }
                />
              ))}
            </div>
          )}
          {showSkipTips && (
            <button
              type='button'
              onClick={handleSkipTips}
              className='ml-auto whitespace-nowrap text-sm font-medium text-gray-500 hover:text-gray-700'
            >
              Skip tips
            </button>
          )}
          {showBack && (
            <button
              type='button'
              onClick={() => dispatch(previousTip())}
              className='ml-auto whitespace-nowrap text-sm font-medium text-gray-500 hover:text-gray-700'
            >
              Back
            </button>
          )}
          <button
            type='button'
            onClick={() => (isLastTip ? handleGotIt() : dispatch(nextTip()))}
            className={clsx(
              'whitespace-nowrap rounded-md bg-green-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-green-700',
              !isMultiTip && 'ml-auto'
            )}
          >
            {isLastTip ? 'Got it' : 'Next'}
          </button>
        </div>
      </div>
    </FloatingPortal>
  );
}
